<template>
    <!-- Loading State -->
    <v-container v-if="loading">
        <v-row>
            <v-col cols="12" class="text-center">
                <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
            </v-col>
        </v-row>
    </v-container>

    <!-- Error State -->
    <v-container v-else-if="error">
        <v-row>
            <v-col cols="12" class="text-center error">
                {{ error }}
            </v-col>
        </v-row>
    </v-container>

    <!-- Success State - Evaluation Loaded -->
    <v-container v-else-if="evaluation">
        <v-row>
            <v-col cols="12">
                <h1>{{ $t('psychologistViewEvaluation.evaluation') }}</h1>
                <v-card class="pa-4" v-if="evaluation && evaluation.client">
                    <v-card-title class="text-h6 mb-3">
                        <v-icon left>mdi-clipboard-text</v-icon>
                        {{ $t('psychologistViewEvaluation.evaluation_details') }}
                    </v-card-title>

                    <v-card-text>
                        <div class="mb-3">
                            <strong>{{ $t('psychologistViewEvaluation.evaluation_for') }}</strong> {{ evaluation.client.first_name }} {{
                            evaluation.client.last_name }}
                        </div>

                        <div class="mb-3">
                            <strong>{{ $t('psychologistViewEvaluation.time_to_complete') }}</strong>
                            {{ timeToComplete.min }} - {{ timeToComplete.max }} {{ $t('psychologistViewEvaluation.minutes') }}
                        </div>

                        <div class="mb-3">
                            <strong>{{ $t('psychologistViewEvaluation.created_on') }}</strong>
                            {{ formatDate(evaluation.createdOn) }}
                            {{ formatTime(evaluation.createdOn) }}
                        </div>

                        <div class="mb-3" v-if="evaluation.completedOn">
                            <strong>{{ $t('psychologistViewEvaluation.completed_on') }}</strong>
                            {{ formatDate(evaluation.completedOn) }}
                            {{ formatTime(evaluation.completedOn) }}
                        </div>

                        <div class="mb-3">
                            <strong>{{ $t('psychologistViewEvaluation.status') }}</strong>
                            <v-chip :color="getStatusColor(evaluation.status)" size="small" class="ml-2">
                                {{ evaluation.status }}
                            </v-chip>
                            <!-- <v-btn v-if="evaluation.status != 'fresh'">{{ $t('psychologistViewEvaluation.reset') }}</v-btn> -->
                        </div>

                        <div class="mb-3">
                            <strong>{{ $t('psychologistViewEvaluation.language') }}</strong>
                            <v-chip size="small" class="ml-2">
                                {{ language(evaluation.language) }}
                            </v-chip>
                            <!-- <v-btn v-if="evaluation.status != 'fresh'">{{ $t('psychologistViewEvaluation.reset') }}</v-btn> -->
                        </div>

                        <div v-if="evaluation.scales && evaluation.scales.length > 0">
                            <strong>{{ $t('psychologistViewEvaluation.scales') }}</strong>
                            <div class="mt-2">
                                <v-chip v-for="scale in evaluation.scales" :key="scale.scale_id" class="mr-2 mb-1"
                                    variant="outlined">
                                    {{ scale.name }}
                                </v-chip>
                            </div>
                        </div>
                    </v-card-text>
                </v-card>

            </v-col>
        </v-row>
        <v-row class="mt-2">
            <v-col cols="12">
                <v-btn prepend-icon="mdi-eye" @click="openPreview">
                    {{ $t('psychologistViewEvaluation.preview') }}
                </v-btn>
            </v-col>
        </v-row>

        <v-row>
            <v-col cols="8" md="10">
                <v-text-field title="Link" v-model="url"></v-text-field>
            </v-col>
            <v-col cols="2" md="1">
                <v-btn icon size="small" @click="shareLink">
                    <v-icon>mdi-share</v-icon>
                </v-btn>
            </v-col>
            <v-col cols="2" md="1">
                <v-btn icon size="small" @click="copyLink">
                    <v-icon>mdi-content-copy</v-icon>
                </v-btn>
            </v-col>
        </v-row>

        <template v-if="evaluation.status == 'completed'">
            <h2>{{ $t('psychologistViewEvaluation.results') }}</h2>
            <v-row v-for="scale in evaluation.scales">
                <v-col>
                    <component :is="`Score${scale.code.toUpperCase()}`" :scale="scale" />
                </v-col>
            </v-row>
        </template>
        <!-- Preview dialog -->
        <v-dialog v-model="previewDialog" max-width="800" scrollable>
            <v-card>
                <v-card-title>{{ $t('psychologistViewEvaluation.preview') }}</v-card-title>
                <v-divider />
                <v-card-text>
                    <v-progress-circular v-if="previewLoading" indeterminate color="primary" class="d-flex mx-auto my-4" />
                    <template v-else>
                        <ScaleComponent
                            v-for="(scale, idx) in previewScales"
                            :key="idx"
                            :rawData="scale"
                        />
                    </template>
                </v-card-text>
                <v-divider />
                <v-card-actions>
                    <v-spacer />
                    <v-btn @click="previewDialog = false">{{ $t('psychologistViewEvaluation.preview_close') }}</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

    </v-container>

    <!-- Fallback - No Data -->
    <v-container v-else>
        <v-row>
            <v-col cols="12" class="text-center warning">
                {{ $t('psychologistViewEvaluation.no_evaluation_found') }}
            </v-col>
        </v-row>
    </v-container>

</template>

<script>
import apiService from '@/services/ApiService'
import {formatDate, formatTime} from '@/utils/time.js'
import { isMobileDevice } from '@/utils/browser.js'
import ScoreGQ6 from '@/components/common/scales/ScoreGQ6.vue'
import ScoreMOCK2 from '@/components/common/scales/ScoreMOCK2.vue'
import ScoreMOCK3 from '@/components/common/scales/ScoreMOCK3.vue'
import ScoreGAD7 from '@/components/common/scales/ScoreGAD7.vue'
import ScoreDASS21 from '@/components/common/scales/ScoreDASS21.vue'
import ScorePHQ9 from '@/components/common/scales/ScorePHQ9.vue'
import ScoreYSQ114 from '@/components/common/scales/ScoreYSQ114.vue'
import langMixin from '@/mixins/langMixin'
import ScaleComponent from '@/components/common/ScaleComponent.vue'

export default {
    name: 'PsychologistViewEvaluationView',
    mixins: [
        langMixin
    ],
    components: {
        ScaleComponent,
        ScoreGQ6,
        ScoreMOCK2,
        ScoreMOCK3,
        ScoreGAD7,
        ScoreDASS21,
        ScorePHQ9,
        ScoreYSQ114
    },
    data() {
        return {
            loading: false,
            error: null,
            // evaluationId: '',
            token: '',
            evaluation: null,
            previewDialog: false,
            previewLoading: false,
            previewScales: [],
        }
    },

    async created() {
        // this.evaluationId = this.$route.params.evaluationId
        this.token = this.$route.params.token
        // console.log('Evaluation id:', this.evaluationId)
        console.log('PsychologistViewEvaluationView > created > Evaluation token (raw):', this.$route.params.token)
        console.log('PsychologistViewEvaluationView > created > Evaluation token:', this.token)

        try {
            this.loading = true
            // let evaluation = await apiService.getEvaluationById(this.evaluationId)
            let evaluation = await apiService.getEvaluationByToken(this.token)
            console.log('Response:', evaluation);

            this.evaluation = evaluation.data;
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            this.loading = false;
        }
    },

    async mounted() {
    },

    computed: {
        url() {
            // this.baseURL = process.env.VUE_APP_API_ENDPOINT
            let url = `${this.appURL}/evaluation/${this.evaluation.token}`
            return url;
        },
        appURL() {
            return `${process.env.VUE_APP_PROTOCOL}://${process.env.VUE_APP_HOST}:${process.env.VUE_APP_PORT}`
        },

        intepretation() {
            let interpretation = '';
            if (this.evaluation) {
                interpretation = 'Să interpretăm....'
            }
            return interpretation;
        },

        timeToComplete(){      
            let min = 0, max = 0;
            for(let idx = 0; idx < this.evaluation.scales.length; idx++){
                let scale = this.evaluation.scales[idx]
                min += scale.min_completion_time
                max += scale.max_completion_time
            }
            return {
                min: min,
                max: max
            }
        }

    }, //end computed

    mounted() {
        console.log('All env vars:', process.env)
        console.log('Protocol:', process.env.VUE_APP_API_PROTOCOL)
        console.log('Host:', process.env.VUE_APP_API_HOST)
        console.log('Port:', process.env.VUE_APP_API_PORT)
    },

    methods: {
        formatDate,
        formatTime,
        
        async openPreview() {
            this.previewDialog = true;
            if (this.previewScales.length > 0) return; // already loaded
            try {
                this.previewLoading = true;
                const response = await apiService.getItems(this.token);
                this.previewScales = response.data;
            } catch (error) {
                console.error('Error loading preview:', error);
            } finally {
                this.previewLoading = false;
            }
        },

        getStatusColor(status) {
            switch (status) {
                case 'fresh': return 'blue';
                case 'started': return 'orange';
                case 'completed': return 'green';
                default: return 'grey';
            }
        },

        async copyLink() {
            console.log('Copy link button pressed');
            let copied = await this.copyToClipboard(this.url)
            if (copied) {
                this.$toast.success(this.$t('psychologistViewEvaluation.link_copied_successfully'))
            }
            else {
                this.$toast.error(this.$t('psychologistViewEvaluation.link_could_not_be_copied_to_clipboard'))
            }
        },

        async shareLink() {
            console.log('Share link button pressed');
            let shared = await this.shareContent(this.url)
            if (shared.result) {
                this.$toast.success(this.$t('psychologistViewEvaluation.link_shared_successfully'))
            }
            else {
                this.$toast.error(`${this.$t('psychologistViewEvaluation.link_could_not_be_shared')} Reason: ${shared.msg}`)
            }
        },

        /**
         *  Secure context: This feature is available only in secure contexts (HTTPS), 
         * in some or all supporting browsers.
         * @see https://developer.mozilla.org/en-US/docs/Web/API/Navigator/share
         */
        async shareContent(text) {
            // Run this in your mobile Chrome console
            console.log('=== Comprehensive Share Debug ===');
            console.log('User Agent:', navigator.userAgent);
            console.log('navigator.share:', navigator.share);
            console.log('navigator keys containing "share":', Object.getOwnPropertyNames(navigator).filter(key => key.toLowerCase().includes('share')));
            console.log('window.isSecureContext:', window.isSecureContext);
            console.log('location.href:', location.href);

            // Test if it's a conditional availability issue
            console.log('=== Conditional Checks ===');
            console.log('Is HTTPS:', location.protocol === 'https:');
            console.log('Is localhost:', location.hostname === 'localhost');
            console.log('Is 127.0.0.1:', location.hostname === '127.0.0.1');
            console.log('Is private IP:', /^(10\.|172\.1[6-9]\.|172\.2[0-9]\.|172\.3[01]\.|192\.168\.)/.test(location.hostname));

            // Check if share exists but is somehow falsy
            console.log('=== Share API Existence ===');
            console.log('navigator.share === undefined:', navigator.share === undefined);
            console.log('navigator.share === null:', navigator.share === null);
            console.log('navigator.share === false:', navigator.share === false);
            console.log('"share" in navigator:', 'share' in navigator);

            const shareData = {
                title: 'Psychological Assessment',
                text: 'Please complete your questionnaire',
                url: text
            };

            try {
                if (!navigator.share) {
                    return {
                        result: false,
                        msg: "Sharing is not supported in this browser"
                    };
                }
                if (!window.isSecureContext) {
                    return {
                        result: false,
                        msg: "Sharing is possible only in a secured context"
                    };
                }
                await navigator.share(shareData);
            } catch (err) {
                console.error('Failed to share link: ', err);
                return {
                    result: false,
                    msg: `Sharing failed. Error: ${err.message}`
                };
            }
            return {
                result: true,
                msg: "All good"
            };
        },

        async copyToClipboard(text) {
            let copied = false;
            try {
                await navigator.clipboard.writeText(text);
                copied = true;
            } catch (err) {
                // Fallback for older browsers
                copied = this.fallbackCopyTextToClipboard(text);
            }
            return copied;
        },

        // Fallback method for older browsers
        fallbackCopyTextToClipboard(text) {
            let copied = false;
            const textArea = document.createElement("textarea");
            textArea.value = text;
            textArea.style.top = "0";
            textArea.style.left = "0";
            textArea.style.position = "fixed";
            document.body.appendChild(textArea);
            textArea.focus();
            textArea.select();

            try {
                const successful = document.execCommand('copy');
                copied = successful ?? false;
            } catch (err) {
                console.error('Failed to copy text: ', err);
            }

            document.body.removeChild(textArea);
            return copied;
        }
    },


}
</script>

<style scoped></style>